/**
 * Reusable rail button leaf component for {@link SidebarRail}.
 *
 * Extracted from SidebarRail.tsx. In manager ("Module settings") mode it renders
 * as a Framer `Reorder.Item` so the categories can be dragged into a new order —
 * smoothly and on touch — via a 3-dot grip on the left.
 */

import type { RefObject } from 'react';
import { Reorder, useDragControls } from 'framer-motion';

/** Reusable rail button */
export function RailItem({
  railKey,
  label,
  activeKey,
  expanded,
  onClick,
  onHover,
  badge,
  count,
  isGroupOpen,
  subItems,
  onSubItemClick,
  dimmed,
  toggle,
  reorderValue,
  draggable,
  shake,
  onReorderEnd,
  dragConstraints,
  children,
}: {
  railKey: string;
  label: string;
  activeKey: string | null;
  expanded: boolean;
  onClick: (key: string) => void;
  /** Fired on pointer-enter / focus — used to warm the module's chunk + data
   *  before a click. Purely additive; callers that omit it are unaffected. */
  onHover?: (key: string) => void;
  badge?: boolean;
  count?: number;
  isGroupOpen?: boolean;
  subItems?: Array<{ id: string; label: string }>;
  onSubItemClick?: (moduleId: string) => void;
  dimmed?: boolean;
  toggle?: { on: boolean; onToggle: () => void };
  /** When set, render as a Framer Reorder.Item with this value (the rail key) so
   *  the item lives in the shared Reorder layout and animates smoothly when the
   *  list re-sorts (e.g. a module toggled on/off slides to its group boundary). */
  reorderValue?: string;
  /** Off / plugin rows stay in the Reorder.Group (so they animate) but must not
   *  start a drag — `draggable={false}` hides the grip and the drag never arms. */
  draggable?: boolean;
  /** Briefly true to play a "not allowed" shake (e.g. disabling the last module). */
  shake?: boolean;
  /** Fired when a reorder drag ends, so the parent can persist the new order. */
  onReorderEnd?: () => void;
  /** Container ref the drag is clamped to, so the item can't leave the list. */
  dragConstraints?: RefObject<HTMLElement | null>;
  children: React.ReactNode;
}) {
  const reorderMode = reorderValue !== undefined;
  const canDrag = reorderMode && draggable !== false;
  const dragControls = useDragControls();
  const showExpander = expanded && count !== undefined && count >= 2 && !toggle;

  const groupClassName = `rail-item-group${dimmed ? ' dimmed' : ''}${reorderMode ? ' reorderable' : ''}${shake ? ' shake' : ''}`;

  const inner = (
    <>
      <button
        className={`rail-btn${activeKey === railKey && !dimmed ? ' active' : ''}`}
        onClick={() => onClick(railKey)}
        // Warm the module chunk + data on hover/focus intent. Suppressed in
        // reorder mode so dragging a row doesn't spam prefetch. The handler is
        // idempotent, so this gate is for cleanliness, not correctness.
        onMouseEnter={!reorderMode ? () => onHover?.(railKey) : undefined}
        onFocus={!reorderMode ? () => onHover?.(railKey) : undefined}
      >
        {canDrag && expanded && (
          // 3-dot grip on the far left — drag starts from here (the rest of the
          // row still navigates / toggles). Drag is pointer-based so it works on
          // touch too.
          <span
            className="rail-reorder-handle"
            aria-label="Drag to reorder"
            onPointerDown={(e) => { e.stopPropagation(); dragControls.start(e); }}
            onClick={(e) => e.stopPropagation()}
          >
            <span className="rail-reorder-dot" />
            <span className="rail-reorder-dot" />
            <span className="rail-reorder-dot" />
          </span>
        )}
        <span className="rail-btn-icon">{children}</span>
        {badge && !dimmed && <span className="rail-badge" />}
        <span className="rail-btn-label">{label}</span>
        {toggle && expanded && (
          <button
            className={`manager-toggle${toggle.on ? ' on' : ''}`}
            onClick={(e) => { e.stopPropagation(); toggle.onToggle(); }}
          />
        )}
        {showExpander && <span className="rail-count-badge">{count}</span>}
        {showExpander && (
          <svg
            width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
            className={`rail-chevron${isGroupOpen ? ' open' : ''}`}
          >
            <polyline points="6 9 12 15 18 9" />
          </svg>
        )}
        {!expanded && <span className="rail-tooltip">{label}</span>}
      </button>
      {showExpander && isGroupOpen && subItems && (
        <div className="rail-sub-items">
          {subItems.map((item) => (
            <button
              key={item.id}
              className="rail-sub-item"
              onClick={() => onSubItemClick?.(item.id)}
            >
              {item.label}
            </button>
          ))}
        </div>
      )}
    </>
  );

  if (reorderMode) {
    // Every manage-mode row (on, off, plugin) is a Reorder.Item so it shares the
    // group's layout context and animates when the list re-sorts. Only `canDrag`
    // rows arm the pointer drag (off/plugin rows render no grip and stay put).
    return (
      <Reorder.Item
        value={reorderValue}
        as="div"
        className={groupClassName}
        drag={canDrag ? 'y' : false}
        dragListener={false}
        dragControls={dragControls}
        dragConstraints={dragConstraints}
        dragElastic={0.04}
        onDragEnd={() => onReorderEnd?.()}
      >
        {inner}
      </Reorder.Item>
    );
  }

  return (
    <div className={groupClassName}>
      {inner}
    </div>
  );
}
